import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import {
  FREEE_DAILY_SALES_QUERY,
  FREEE_INTEGRATION_STATUS_QUERY,
  STORES_QUERY,
  SYNC_FREEE_DAILY_SALES_MUTATION,
} from '../graphql/queries';
import { gql } from '../lib/api';

interface Store { id: string; name: string }
interface IntegrationStatus {
  companyId: string;
  connected: boolean;
  refreshConfigured: boolean;
  scheduleEnabled: boolean;
  lastAttemptAt: string | null;
  lastSuccessAt: string | null;
  lastTargetDate: string | null;
  lastStatus: string | null;
  lastMessage: string | null;
}
interface DailySales {
  storeId: string;
  date: string;
  companyId: string;
  sectionId: string;
  sectionName: string;
  amount: number;
  dealCount: number;
  details: unknown;
  paymentTotals: unknown;
  syncedAt: string;
}
interface DealDetail {
  freeeDealId: number;
  amount: number;
  refNumber?: string | null;
  dealOriginName?: string | null;
  lines?: Array<{ freeeDetailId: number; description?: string | null; amount: number }>;
  payments?: Array<{ freeePaymentId: number; walletableName: string; amount: number }>;
}
interface DisplaySalesRow {
  row: DailySales;
  payments: Array<[string, number]>;
  details: DealDetail[];
  rowKey: string;
}

const dateInJapan = (offsetDays = 0) => {
  const date = new Date(Date.now() + 9 * 60 * 60 * 1000);
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
};

const firstOfMonth = () => `${dateInJapan().slice(0, 7)}-01`;
const ALL_STORES = 'all';
const WEEKDAYS_JA = ['日', '月', '火', '水', '木', '金', '土'];
const validDateParam = (value: string | null, fallback: string) =>
  value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : fallback;
const yen = new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 });
const formatDateTime = (value: string | null) =>
  value ? new Intl.DateTimeFormat('ja-JP', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '—';
const formatSalesDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const [, year, month, day] = match;
  const weekday = WEEKDAYS_JA[new Date(Date.UTC(Number(year), Number(month) - 1, Number(day))).getUTCDay()];
  return `${year}年${month}月${day}日（${weekday}）`;
};
const compareWalletableNames = (a: string, b: string) => a.localeCompare(b, 'ja');
const dealWalletableKey = (deal: DealDetail) =>
  (deal.payments ?? [])
    .map((payment) => payment.walletableName)
    .sort(compareWalletableNames)
    .join('\u0000') || '\uffff';

const parseJsonObject = <T extends Record<string, unknown>>(value: unknown): T => {
  let parsed = value;
  try {
    for (let depth = 0; depth < 3 && typeof parsed === 'string'; depth += 1) {
      parsed = JSON.parse(parsed) as unknown;
    }
  } catch {
    return {} as T;
  }
  return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)
    ? parsed as T
    : {} as T;
};

export const FreeeSalesPage = ({ isAdmin }: { isAdmin: boolean }) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [storeId, setStoreId] = useState(() => searchParams.get('store') ?? ALL_STORES);
  const [from, setFrom] = useState(() => validDateParam(searchParams.get('from'), firstOfMonth()));
  const [to, setTo] = useState(() => validDateParam(searchParams.get('to'), dateInJapan()));
  const [syncDate, setSyncDate] = useState(() => dateInJapan(-1));
  const [expanded, setExpanded] = useState<string | null>(null);

  const storesQuery = useQuery({
    queryKey: ['stores'],
    queryFn: () => gql<{ stores: Store[] }>(STORES_QUERY),
  });
  const stores = storesQuery.data?.stores ?? [];
  const selectedStoreIds = storeId === ALL_STORES
    ? stores.map((store) => store.id)
    : [storeId];

  useEffect(() => {
    const next = new URLSearchParams();
    next.set('store', storeId);
    next.set('from', from);
    next.set('to', to);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [storeId, from, to, searchParams, setSearchParams]);

  const statusQuery = useQuery({
    queryKey: ['freeeIntegrationStatus'],
    queryFn: () => gql<{ freeeIntegrationStatus: IntegrationStatus }>(FREEE_INTEGRATION_STATUS_QUERY),
  });
  const salesQueries = useQueries({
    queries: selectedStoreIds.map((selectedStoreId) => ({
      queryKey: ['freeeDailySales', selectedStoreId, from, to],
      queryFn: () => gql<{ freeeDailySales: DailySales[] }>(FREEE_DAILY_SALES_QUERY, { storeId: selectedStoreId, from, to }),
      enabled: Boolean(selectedStoreId && from && to),
    })),
  });
  const syncMutation = useMutation({
    mutationFn: (date: string) => gql<{ syncFreeeDailySales: { status: string; message: string } }>(SYNC_FREEE_DAILY_SALES_MUTATION, { date }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['freeeDailySales'] });
      void queryClient.invalidateQueries({ queryKey: ['freeeIntegrationStatus'] });
    },
  });

  const rows = useMemo(
    () => salesQueries
      .flatMap((query) => query.data?.freeeDailySales ?? [])
      .sort((a, b) => b.date.localeCompare(a.date) || a.sectionName.localeCompare(b.sectionName, 'ja')),
    [salesQueries],
  );
  const total = useMemo(() => rows.reduce((sum, row) => sum + row.amount, 0), [rows]);
  const displayRows = useMemo<DisplaySalesRow[]>(
    () => rows.map((row) => ({
      row,
      payments: Object.entries(parseJsonObject<Record<string, number>>(row.paymentTotals))
        .sort(([a], [b]) => compareWalletableNames(a, b)),
      details: Object.values(parseJsonObject<Record<string, DealDetail>>(row.details))
        .sort((a, b) => compareWalletableNames(dealWalletableKey(a), dealWalletableKey(b)) || a.freeeDealId - b.freeeDealId),
      rowKey: `${row.storeId}#${row.date}`,
    })),
    [rows],
  );
  const isSalesLoading = storesQuery.isLoading || salesQueries.some((query) => query.isLoading);
  const status = statusQuery.data?.freeeIntegrationStatus;

  return (
    <div style={page}>
      <section className="freee-sales-hero" style={hero}>
        <div>
          <p style={eyebrow}>DAILY SALES FLASH / FREEE ACCOUNTING</p>
          <h1 style={title}>売上速報</h1>
          <p style={lead}>freee会計に連携された前日の売上を、店舗別・口座別に確認できます。</p>
        </div>
        {isAdmin ? <div style={syncPanel}>
          <label style={label} htmlFor="sync-date">取得対象日</label>
          <div style={inlineControls}>
            <input id="sync-date" type="date" value={syncDate} max={dateInJapan()} onChange={(event) => setSyncDate(event.target.value)} style={control} />
            <button type="button" disabled={syncMutation.isPending || !syncDate} onClick={() => syncMutation.mutate(syncDate)} style={primaryButton}>
              {syncMutation.isPending ? '取得中…' : '今すぐ同期'}
            </button>
          </div>
          {syncMutation.data && <p style={successText}>{syncMutation.data.syncFreeeDailySales.message}</p>}
          {syncMutation.isError && <p style={errorText}>同期に失敗しました。接続情報とトークンを確認してください。</p>}
        </div> : <div style={syncPanel}><span style={statusLabel}>AUTOMATIC UPDATE</span><strong style={{ display: 'block', marginTop: '.4rem' }}>毎日 10:00 JST 更新</strong><p style={{ ...lead, marginTop: '.35rem' }}>対象日が定休日の月曜日の場合は取得をスキップします。</p></div>}
      </section>

      <section style={statusGrid} aria-label="連携状態">
        <StatusCard label="本番事業所" value={status?.companyId ?? '—'} sub="株式会社LOOP" />
        <StatusCard label="自動同期" value={status?.scheduleEnabled ? '稼働中' : '停止中'} sub="毎日 10:00 JST" tone={status?.scheduleEnabled ? 'good' : 'warn'} />
        <StatusCard label="最終成功" value={formatDateTime(status?.lastSuccessAt ?? null)} sub={status?.lastTargetDate ? `対象 ${formatSalesDate(status.lastTargetDate)}` : '同期履歴なし'} />
        <StatusCard label="最終結果" value={status?.lastStatus ?? '—'} sub={status?.lastMessage ?? '待機中'} tone={status?.lastStatus === 'FAILED' ? 'warn' : undefined} />
      </section>

      {isAdmin && status && (!status.refreshConfigured || !status.scheduleEnabled) && (
        <div style={notice}>
          OAuthの長期運用情報が揃うまで自動同期は停止します。手動同期は有効なアクセストークンで実行できます。
        </div>
      )}

      <section style={card}>
        <div className="freee-sales-filters" style={filters}>
          <label style={label}>店舗<select value={storeId} onChange={(event) => setStoreId(event.target.value)} style={control}><option value={ALL_STORES}>すべて（2店舗）</option>{stores.map((store) => <option key={store.id} value={store.id}>{store.name}</option>)}</select></label>
          <label style={label}>開始日<input type="date" value={from} onChange={(event) => setFrom(event.target.value)} style={control} /></label>
          <label style={label}>終了日<input type="date" value={to} onChange={(event) => setTo(event.target.value)} style={control} /></label>
          <div className="freee-sales-total" style={totalBox}><span>{storeId === ALL_STORES ? '2店舗合計' : '期間合計'}</span><strong>{yen.format(total)}</strong></div>
        </div>

        {isSalesLoading ? <p>売上データを読み込んでいます…</p> : rows.length === 0 ? (
          <div style={empty}>選択期間のfreee売上はまだ同期されていません。</div>
        ) : (
          <>
          <div className="freee-sales-table" style={{ overflowX: 'auto' }}>
            <table style={table}>
              <thead><tr><th style={th}>日付</th><th style={th}>部門</th><th style={th}>取引数</th><th style={th}>収入額</th><th style={th}>口座別</th><th style={th}>明細</th></tr></thead>
              <tbody>{displayRows.map(({ row, payments, details, rowKey }) => {
                const detailId = `sales-detail-${row.storeId}-${row.date}`;
                return [
                  <tr key={rowKey}><td style={td}>{formatSalesDate(row.date)}</td><td style={td}>{row.sectionName}</td><td style={td}>{row.dealCount}件</td><td style={{ ...td, fontWeight: 800 }}>{yen.format(row.amount)}</td><td style={td}>{payments.map(([name, amount]) => <span key={name} style={chip}>{name} {yen.format(amount)}</span>)}</td><td style={td}><button type="button" style={textButton} aria-expanded={expanded === rowKey} aria-controls={detailId} onClick={() => setExpanded(expanded === rowKey ? null : rowKey)}>{expanded === rowKey ? '閉じる' : '取引を表示'}</button></td></tr>,
                  expanded === rowKey && <tr key={`${rowKey}-detail`}><td id={detailId} colSpan={6} style={detailCell}><DealDetails details={details} /></td></tr>,
                ];
              })}</tbody>
            </table>
          </div>
          <div className="freee-sales-mobile" aria-label="日次売上一覧">
            {displayRows.map(({ row, payments, details, rowKey }) => {
              const detailId = `mobile-sales-detail-${row.storeId}-${row.date}`;
              const isExpanded = expanded === rowKey;
              return (
                <article className="freee-sales-mobile-card" key={rowKey}>
                  <div className="freee-sales-mobile-heading">
                    <div>
                      <time dateTime={row.date}>{formatSalesDate(row.date)}</time>
                      <strong>{row.sectionName}</strong>
                    </div>
                    <div className="freee-sales-mobile-amount">
                      <span>収入額</span>
                      <strong>{yen.format(row.amount)}</strong>
                    </div>
                  </div>
                  <div className="freee-sales-mobile-count">取引数 <strong>{row.dealCount}件</strong></div>
                  <dl className="freee-sales-mobile-payments">
                    {payments.map(([name, amount]) => <div key={name}><dt>{name}</dt><dd>{yen.format(amount)}</dd></div>)}
                  </dl>
                  <button className="freee-sales-mobile-button" type="button" aria-expanded={isExpanded} aria-controls={detailId} onClick={() => setExpanded(isExpanded ? null : rowKey)}>
                    {isExpanded ? '取引明細を閉じる' : '取引明細を表示'}
                  </button>
                  {isExpanded && <div className="freee-sales-mobile-details" id={detailId}><DealDetails details={details} /></div>}
                </article>
              );
            })}
          </div>
          </>
        )}
      </section>
    </div>
  );
};

const StatusCard = ({ label: cardLabel, value, sub, tone }: { label: string; value: string; sub: string; tone?: 'good' | 'warn' }) => (
  <article style={{ ...statusCard, borderTopColor: tone === 'good' ? '#0b8f87' : tone === 'warn' ? '#aa0b56' : '#111' }}><span style={statusLabel}>{cardLabel}</span><strong style={statusValue}>{value}</strong><small style={statusSub}>{sub}</small></article>
);

const DealDetails = ({ details }: { details: DealDetail[] }) => (
  <>{details.map((deal) => (
    <div className="freee-sales-deal" key={deal.freeeDealId} style={dealRow}>
      <strong>freee #{deal.freeeDealId}</strong>
      <span>{deal.lines?.map((line) => line.description).filter(Boolean).join(' / ') || '摘要なし'}</span>
      <span>{[...(deal.payments ?? [])].sort((a, b) => compareWalletableNames(a.walletableName, b.walletableName)).map((payment) => `${payment.walletableName} ${yen.format(payment.amount)}`).join(' / ')}</span>
      <strong>{yen.format(deal.amount)}</strong>
    </div>
  ))}</>
);

const page: React.CSSProperties = { display: 'grid', gap: '1.25rem' };
const hero: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(280px, .8fr)', gap: '1rem', alignItems: 'end', borderBottom: '2px solid #111', paddingBottom: '1.25rem' };
const eyebrow: React.CSSProperties = { margin: 0, color: '#aa0b56', fontSize: '.72rem', fontWeight: 800, letterSpacing: '.16em' };
const title: React.CSSProperties = { margin: '.25rem 0', fontFamily: 'Georgia, serif', fontSize: 'clamp(2rem, 5vw, 4.6rem)', lineHeight: .95, letterSpacing: '-.045em' };
const lead: React.CSSProperties = { margin: '.8rem 0 0', color: '#616161', lineHeight: 1.7 };
const syncPanel: React.CSSProperties = { border: '1px solid #111', padding: '1rem', background: '#fff' };
const inlineControls: React.CSSProperties = { display: 'flex', gap: '.5rem', marginTop: '.4rem', flexWrap: 'wrap' };
const label: React.CSSProperties = { display: 'grid', gap: '.35rem', fontSize: '.74rem', fontWeight: 800, letterSpacing: '.05em' };
const control: React.CSSProperties = { minHeight: 40, padding: '.5rem .65rem', border: '1px solid #9c9997', background: '#fff' };
const primaryButton: React.CSSProperties = { minHeight: 40, padding: '.5rem 1rem', border: '1px solid #111', background: '#111', color: '#fff', fontWeight: 800, cursor: 'pointer' };
const successText: React.CSSProperties = { margin: '.65rem 0 0', color: '#087f5b', fontSize: '.85rem' };
const errorText: React.CSSProperties = { margin: '.65rem 0 0', color: '#aa0b56', fontSize: '.85rem' };
const statusGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.75rem' };
const statusCard: React.CSSProperties = { display: 'grid', gap: '.35rem', padding: '1rem', border: '1px solid #d4d1cf', borderTop: '3px solid #111', background: '#fff' };
const statusLabel: React.CSSProperties = { color: '#777', fontSize: '.68rem', fontWeight: 800, letterSpacing: '.1em' };
const statusValue: React.CSSProperties = { fontSize: '1.1rem' };
const statusSub: React.CSSProperties = { color: '#777', lineHeight: 1.4 };
const notice: React.CSSProperties = { padding: '.85rem 1rem', borderLeft: '4px solid #d6a400', background: '#fff8d8', lineHeight: 1.6 };
const card: React.CSSProperties = { border: '1px solid #d4d1cf', background: '#fff', padding: '1rem' };
const filters: React.CSSProperties = { display: 'flex', alignItems: 'end', gap: '.75rem', flexWrap: 'wrap', borderBottom: '1px solid #d4d1cf', paddingBottom: '1rem', marginBottom: '1rem' };
const totalBox: React.CSSProperties = { marginLeft: 'auto', display: 'grid', textAlign: 'right', gap: '.15rem' };
const empty: React.CSSProperties = { padding: '3rem 1rem', color: '#777', textAlign: 'center', background: '#f7f6f4' };
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', minWidth: 800 };
const th: React.CSSProperties = { padding: '.65rem', borderBottom: '2px solid #111', textAlign: 'left', fontSize: '.72rem', letterSpacing: '.08em' };
const td: React.CSSProperties = { padding: '.75rem .65rem', borderBottom: '1px solid #dedbd8', verticalAlign: 'top' };
const chip: React.CSSProperties = { display: 'inline-block', margin: '0 .3rem .2rem 0', padding: '.2rem .4rem', background: '#e5f7f5', fontSize: '.78rem' };
const textButton: React.CSSProperties = { border: 0, borderBottom: '1px solid currentColor', background: 'transparent', color: '#007f88', cursor: 'pointer', padding: 0 };
const detailCell: React.CSSProperties = { padding: '.75rem', background: '#f7f6f4', borderBottom: '1px solid #111' };
const dealRow: React.CSSProperties = { display: 'grid', gridTemplateColumns: '140px minmax(180px, 1fr) minmax(160px, .7fr) 100px', gap: '.75rem', padding: '.55rem 0', borderBottom: '1px dotted #c7c3c0', alignItems: 'center', fontSize: '.82rem' };
